"use client";

import { useTranslations } from "use-intl";

import { EntityAbout } from "@/components/catalog-entity/entity-about";
import { EntityAboutMore } from "@/components/catalog-entity/entity-about-more";
import type { EntityAboutSection } from "@/components/catalog-entity/entity-about-section";
import { useLinksAboutSection } from "@/components/catalog-entity/use-links-about-section";
import { FallbackImage } from "@/components/fallback-image";
import { Link } from "@/components/link";
import { withImageRenderParams } from "@garimpa/lib/imageUrls";
import type { CatalogUrl, SocialLink } from "@garimpa/lib/musicTypes";

import { ArtistMemberLedger, type ArtistMemberFact } from "./artist-member-ledger";
import { SimilarArtistsGrid } from "./similar-artists-grid";
import { useSimilarArtists } from "./use-similar-artists";

const MEMBER_PREVIEW = 4;
const FACE_PREVIEW = 5;

type SimilarArtist = { id: string; imageUrl: string; name: string };

interface ArtistAboutProps {
  artistId: string;
  artistName: string;
  members: ArtistMemberFact[];
  officialUrls: CatalogUrl[];
  socialUrls: SocialLink[];
  otherUrls: CatalogUrl[];
}

/** Members, similar artists and links for the artist page's About cards and panel. */
export function ArtistAbout({
  artistId,
  artistName,
  members,
  officialUrls,
  socialUrls,
  otherUrls,
}: ArtistAboutProps) {
  const t = useTranslations("artist");
  const similar = useSimilarArtists(artistId);
  const links = useLinksAboutSection({ officialUrls, socialUrls, otherUrls });
  const previewMembers = pickPreviewMembers(members);

  const sections: EntityAboutSection[] = [
    {
      key: "members",
      label: t("factsMembers"),
      count: members.length,
      preview: (
        <>
          {previewMembers.map((member) => (
            <Link
              key={member.id}
              to={`/artist/${member.id}`}
              className="text-foreground/85 hover:text-primary"
            >
              {member.name}
            </Link>
          ))}
          <EntityAboutMore count={members.length - previewMembers.length} />
        </>
      ),
      content: <ArtistMemberLedger members={members} />,
    },
    {
      key: "similar",
      label: t("similarArtists"),
      count: similar.artists.length,
      loading: similar.isLoading,
      preview: <SimilarFacePile artists={similar.artists} loading={similar.isLoading} />,
      content: <SimilarArtistsGrid artists={similar.artists} />,
    },
    links,
  ];

  return <EntityAbout id={artistId} entityName={artistName} sections={sections} />;
}

function SimilarFacePile({
  artists,
  loading,
}: {
  artists: ReadonlyArray<SimilarArtist>;
  loading: boolean;
}) {
  if (loading) {
    return (
      <span className="flex items-center">
        {Array.from({ length: FACE_PREVIEW }, (_, index) => (
          <span
            key={index}
            className="-ml-2 size-8 animate-pulse rounded-full border-2 border-card bg-secondary first:ml-0"
          />
        ))}
      </span>
    );
  }

  return (
    <>
      <span className="flex items-center">
        {artists.slice(0, FACE_PREVIEW).map((artist) => (
          <Link
            key={artist.id}
            to={`/artist/${artist.id}`}
            aria-label={artist.name}
            title={artist.name}
            className="-ml-2 size-8 shrink-0 overflow-hidden rounded-full border-2 border-card bg-secondary first:ml-0 hover:z-10 hover:border-primary"
          >
            <FallbackImage
              src={withImageRenderParams(artist.imageUrl, { width: 150 })}
              alt=""
              fallback="person"
              className="rounded-full"
              sizes="32px"
            />
          </Link>
        ))}
      </span>
      <EntityAboutMore count={artists.length - FACE_PREVIEW} />
    </>
  );
}

/** Current members first (no end date), then everyone else, one entry per artist. */
function pickPreviewMembers(members: ArtistMemberFact[]): ArtistMemberFact[] {
  const isCurrent = (member: ArtistMemberFact) => Boolean(member.beginDate && !member.endDate);
  const ordered = [...members.filter(isCurrent), ...members.filter((m) => !isCurrent(m))];
  const seen = new Set<string>();
  return ordered
    .filter((member) => {
      if (seen.has(member.id)) return false;
      seen.add(member.id);
      return true;
    })
    .slice(0, MEMBER_PREVIEW);
}
